import React, { useState } from "react";
import {
	Avatar,
	Box,
	Chip,
	IconButton,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import { chronos } from "nhb-toolbox";
import { imgUrl } from "src/Helpers/utils/constants";
import { useApiMutation } from "src/hooks/useApiMutation";
import type { ISessionRequest } from "src/Types/module/b2bSession/b2bSessionRequest.types";
import DeleteConfirmation from "src/Utils/DeleteConfirmation";
import UpdateSessionRequestModal from "./UpdateSessionRequestModal";

interface Props {
	request: ISessionRequest;
	serial?: number;
}

const formatDisplayTime = (timeStr?: string) => {
	if (!timeStr) return "";
	const [hours, minutes] = timeStr.split(":");
	if (hours === undefined || minutes === undefined) return timeStr;
	const dummyDate = dayjs().hour(Number(hours)).minute(Number(minutes));
	return dummyDate.format("hh:mm A");
};

const getStatusChip = (status?: string, rejectedReason?: string | null) => {
	const normalized = (status || "pending").toLowerCase();

	let bg = "rgba(245, 158, 11, 0.12)";
	let color = "#D97706";
	let border = "1px solid rgba(245, 158, 11, 0.3)";
	let label = "Pending";

	if (normalized === "approved") {
		bg = "rgba(16, 185, 129, 0.12)";
		color = "#059669";
		border = "1px solid rgba(16, 185, 129, 0.3)";
		label = "Approved";
	} else if (normalized === "rejected") {
		bg = "rgba(239, 68, 68, 0.12)";
		color = "#DC2626";
		border = "1px solid rgba(239, 68, 68, 0.3)";
		label = "Rejected";
	} else if (normalized === "completed") {
		bg = "rgba(14, 165, 233, 0.12)";
		color = "#0284C7";
		border = "1px solid rgba(14, 165, 233, 0.3)";
		label = "Completed";
	}

	const chip = (
		<Chip
			label={label}
			size="small"
			sx={{
				bgcolor: bg,
				color: color,
				border: border,
				fontWeight: 700,
				fontSize: "0.75rem",
				textTransform: "capitalize",
			}}
		/>
	);

	if (normalized === "rejected" && rejectedReason) {
		return (
			<Tooltip title={`Reason: ${rejectedReason}`} arrow>
				{chip}
			</Tooltip>
		);
	}

	return chip;
};

const SessionRequestTableRow: React.FC<Props> = ({ request, serial }) => {
	const theme = useTheme();
	const [openEdit, setOpenEdit] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);

	const { mutate: deleteRequest, isPending: isDeleting } = useApiMutation<
		void,
		void
	>({
		method: "DELETE",
		endPoint: `/api/fair/admin/sessions/requests/${request.id}`,
		invalidateQueries: ["SessionRequests", "SessionRequest"],
	});

	const handleDelete = () => {
		deleteRequest(undefined, {
			onSuccess: () => {
				setOpenDeleteModal(false);
			},
		});
	};

	const fromFullName = `${request.from_b2b_first_name || ""} ${request.from_b2b_last_name || ""
		}`.trim();

	const toFullName = `${request.to_b2b_first_name || ""} ${request.to_b2b_last_name || ""
		}`.trim();

	const startTimeFormatted = formatDisplayTime(request.start_time);
	const endTimeFormatted = formatDisplayTime(request.end_time);

	return (
		<>
			<TableRow hover>
				{/* SL. */}
				<TableCell>{serial ?? request.id}</TableCell>

				{/* From B2B (Requester) */}
				<TableCell>
					{fromFullName ? (
						<Box display="flex" alignItems="center" gap={1.2}>
							<Avatar
								src={
									request.from_b2b_photo
										? `${imgUrl}/${request.from_b2b_photo}`
										: undefined
								}
								alt={fromFullName}
								sx={{
									width: 34,
									height: 34,
									fontSize: "0.85rem",
									bgcolor: "primary.main",
									fontWeight: 700,
								}}
							>
								{fromFullName.charAt(0).toUpperCase()}
							</Avatar>
							<Box minWidth={0}>
								<Typography variant="body2" fontWeight={600} noWrap>
									{fromFullName}
								</Typography>

							</Box>
						</Box>
					) : (
						<Typography variant="body2" color="text.secondary">
							N/A
						</Typography>
					)}
				</TableCell>

				{/* To B2B (Invited Member) */}
				<TableCell>
					{toFullName ? (
						<Box display="flex" alignItems="center" gap={1.2}>
							<Avatar
								src={
									request.to_b2b_photo
										? `${imgUrl}/${request.to_b2b_photo}`
										: undefined
								}
								alt={toFullName}
								sx={{
									width: 34,
									height: 34,
									fontSize: "0.85rem",
									bgcolor: "secondary.main",
									fontWeight: 700,
								}}
							>
								{toFullName.charAt(0).toUpperCase()}
							</Avatar>
							<Box minWidth={0}>
								<Typography variant="body2" fontWeight={600} noWrap>
									{toFullName}
								</Typography>

							</Box>
						</Box>
					) : (
						<Typography variant="body2" color="text.secondary">
							N/A
						</Typography>
					)}
				</TableCell>

				{/* Session Info */}
				<TableCell>
					<Typography variant="body2" fontWeight={600}>
						{request.session_no || "N/A"}
					</Typography>
					<Typography variant="caption" color="text.secondary" display="block">
						{request.date ? dayjs(request.date).format("YYYY-MM-DD") : "—"}
						{startTimeFormatted && endTimeFormatted
							? ` (${startTimeFormatted} – ${endTimeFormatted})`
							: ""}
					</Typography>
				</TableCell>

				{/* Note */}
				<TableCell sx={{ maxWidth: 220 }}>
					{request.note ? (
						<Tooltip title={request.note} arrow>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									cursor: "pointer",
								}}
							>
								{request.note}
							</Typography>
						</Tooltip>
					) : (
						<Typography variant="caption" color="text.disabled">
							—
						</Typography>
					)}
					{request.rejected_reason && request.status === "rejected" && (
						<Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
							<InfoOutlinedIcon sx={{ fontSize: 13, color: "error.main" }} />
							<Typography
								variant="caption"
								color="error.main"
								sx={{
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								Reason: {request.rejected_reason}
							</Typography>
						</Box>
					)}
				</TableCell>

				{/* Status */}
				<TableCell>
					{getStatusChip(request.status, request.rejected_reason)}
				</TableCell>


				{/* Actions */}
				<TableCell align="right">
					<Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
						<Tooltip title="Edit Request" arrow>
							<IconButton
								onClick={() => setOpenEdit(true)}
								size="small"
								sx={{
									color: theme.palette.success.dark,
									"&:hover": {
										bgcolor: theme.colors.success.lighter,
									},
								}}
							>
								<EditTwoToneIcon fontSize="small" />
							</IconButton>
						</Tooltip>

						<DeleteConfirmation
							handleClose={() => setOpenDeleteModal(false)}
							handleDeleteFun={handleDelete}
							text="Are you sure you want to delete this session request?"
							loading={isDeleting}
						/>
					</Box>
				</TableCell>
			</TableRow>

			{/* Edit Modal */}
			{openEdit && (
				<UpdateSessionRequestModal
					open={openEdit}
					handleClose={() => setOpenEdit(false)}
					request={request}
				/>
			)}
		</>
	);
};

export default SessionRequestTableRow;
